import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'

const inputStyle: React.CSSProperties = {
  background: 'var(--paper)', border: '1px solid var(--edge)', borderRadius: 2,
  padding: '10px 13px', color: 'var(--text)', fontFamily: 'Syne Mono',
  fontSize: '0.78rem', outline: 'none', width: '100%', boxSizing: 'border-box',
}

export default function ForgotPassword() {
  const navigate = useNavigate()
  const [step,     setStep]     = useState<'email' | 'code' | 'newpw'>('email')
  const [email,    setEmail]    = useState('')
  const [token,    setToken]    = useState('')
  const [code,     setCode]     = useState('')
  const [password, setPassword] = useState('')
  const [confirm,  setConfirm]  = useState('')
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState('')

  async function sendCode() {
    if (!email) { setError('Enter your email'); return }
    setLoading(true); setError('')
    try {
      const res = await api.auth.forgotPassword(email)
      setToken(res.token ?? '')
      setStep('code')
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  async function verifyCode() {
    if (code.length !== 6) { setError('Enter the 6-digit code from your email'); return }
    setLoading(true); setError('')
    try {
      await api.auth.verifyResetCode(token, code)
      setStep('newpw')
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  async function setNewPassword() {
    if (!password || !confirm) { setError('Both fields required'); return }
    if (password !== confirm)  { setError('Passwords do not match'); return }
    if (password.length < 8)   { setError('Password must be at least 8 characters'); return }
    setLoading(true); setError('')
    try {
      await api.auth.resetPassword(token, password)
      navigate('/login', { state: { message: 'Password updated — sign in with your new password' } })
    } catch (e: any) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }

  const steps = ['Email', 'Verify Code', 'New Password']
  const stepIndex = step === 'email' ? 0 : step === 'code' ? 1 : 2

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ width: '100%', maxWidth: 380 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <Link to="/" style={{ fontFamily: 'Syne', fontWeight: 800, fontSize: '1.4rem', color: 'var(--bill)', textDecoration: 'none' }}>
            Polywally<span style={{ fontFamily: 'Syne Mono', fontSize: '0.38rem', color: 'var(--cash)', verticalAlign: 'super', marginLeft: 2 }}>™</span>
          </Link>
          <div style={{ fontFamily: 'Syne Mono', fontSize: '0.6rem', letterSpacing: '0.2em', color: 'var(--muted)', marginTop: 4 }}>RESET PASSWORD</div>
        </div>

        <div style={{ background: 'var(--card)', border: '1px solid var(--edge)', borderRadius: 4, padding: 28 }}>

          {/* Step indicator */}
          <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
            {steps.map((label, i) => {
              const active = i === stepIndex
              const done   = i < stepIndex
              return (
                <div key={label} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ height: 2, background: done || active ? 'var(--cash)' : 'var(--edge)', marginBottom: 5, borderRadius: 1 }} />
                  <div style={{ fontFamily: 'Syne Mono', fontSize: '.52rem', letterSpacing: '.1em', textTransform: 'uppercase', color: active || done ? 'var(--cash)' : 'var(--muted)' }}>
                    {done ? '✓ ' : ''}{label}
                  </div>
                </div>
              )
            })}
          </div>

          {/* Step 1 — Email */}
          {step === 'email' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)', lineHeight: 1.7 }}>
                Enter your account email. We'll send a 6-digit verification code.
              </div>
              {error && <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--red)', background: 'rgba(224,92,92,.07)', border: '1px solid rgba(224,92,92,.2)', borderRadius: 2, padding: '8px 10px' }}>{error}</div>}
              <input type="email" placeholder="your@email.com" value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && sendCode()}
                style={inputStyle} />
              <button onClick={sendCode} disabled={loading} style={{ padding: '10px', background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '0.8rem', cursor: 'pointer', opacity: loading ? .5 : 1 }}>
                {loading ? 'Sending...' : 'Send Code →'}
              </button>
              <div style={{ textAlign: 'center', fontFamily: 'Syne Mono', fontSize: '.65rem' }}>
                <Link to="/login" style={{ color: 'var(--cash)', textDecoration: 'none' }}>← Back to sign in</Link>
              </div>
            </div>
          )}

          {/* Step 2 — Enter code */}
          {step === 'code' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)', lineHeight: 1.7 }}>
                A 6-digit code was sent to <span style={{ color: 'var(--text)' }}>{email}</span>. Check your spam folder. Expires in 15 minutes.
              </div>
              {error && <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--red)', background: 'rgba(224,92,92,.07)', border: '1px solid rgba(224,92,92,.2)', borderRadius: 2, padding: '8px 10px' }}>{error}</div>}
              <input
                type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                placeholder="000000" value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                onKeyDown={e => e.key === 'Enter' && verifyCode()}
                style={{ ...inputStyle, fontSize: '1.6rem', letterSpacing: '0.4em', textAlign: 'center', padding: '14px' }}
              />
              <button onClick={verifyCode} disabled={loading || code.length !== 6} style={{ padding: '10px', background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '0.8rem', cursor: code.length === 6 ? 'pointer' : 'not-allowed', opacity: loading || code.length !== 6 ? .5 : 1 }}>
                {loading ? 'Verifying...' : 'Verify Code →'}
              </button>
              <div style={{ textAlign: 'center', fontFamily: 'Syne Mono', fontSize: '.62rem', color: 'var(--muted)' }}>
                Didn't receive it?{' '}
                <button onClick={() => { setStep('email'); setCode(''); setError('') }}
                  style={{ background: 'none', border: 'none', color: 'var(--cash)', fontFamily: 'Syne Mono', fontSize: '.62rem', cursor: 'pointer', padding: 0 }}>
                  Resend code
                </button>
              </div>
            </div>
          )}

          {/* Step 3 — New password */}
          {step === 'newpw' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--muted)', lineHeight: 1.7 }}>
                ✓ Identity verified. Choose a new password.
              </div>
              {error && <div style={{ fontFamily: 'Syne Mono', fontSize: '.65rem', color: 'var(--red)', background: 'rgba(224,92,92,.07)', border: '1px solid rgba(224,92,92,.2)', borderRadius: 2, padding: '8px 10px' }}>{error}</div>}
              <input type="password" placeholder="New password (min 8 chars)" value={password}
                onChange={e => setPassword(e.target.value)}
                style={inputStyle} />
              <input type="password" placeholder="Confirm new password" value={confirm}
                onChange={e => setConfirm(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && setNewPassword()}
                style={inputStyle} />
              <button onClick={setNewPassword} disabled={loading} style={{ padding: '10px', background: 'var(--cash)', color: 'var(--ink)', border: 'none', borderRadius: 2, fontFamily: 'Syne', fontWeight: 800, fontSize: '0.8rem', cursor: 'pointer', opacity: loading ? .5 : 1 }}>
                {loading ? 'Updating...' : 'Update Password →'}
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  )
}
